import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Activity, BadgeDollarSign, BarChart3, Gift, Languages, LayoutDashboard, LogOut, Megaphone, MessageSquare, Settings2, ShieldAlert, TicketPercent, SlidersHorizontal, Users, UserPlus, Wallet } from 'lucide-react';
import Brand from '../components/Brand';
import ModeToggle from '../components/ModeToggle';
import { authAPI } from '../lib/api';
import { clearSession, getUser } from '../lib/auth';
import { getLang, setLang, t } from '../lib/i18n';

// Staff console — the IMS "Velzon" register: a navy command sidebar (grouped
// nav) + a white topbar + a light content canvas, consistent with the IMS/LMS.
const GROUPS = [
  { label: 'Menu', items: [
    { to: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/admin/reports', label: 'Reports', icon: BarChart3 },
    { to: '/admin/affiliates', label: 'Affiliates', icon: Users },
    { to: '/admin/referrals', label: 'Referrals', icon: UserPlus },
    { to: '/admin/ledger', label: 'Ledger', icon: Wallet },
    { to: '/admin/payouts', label: 'Payouts', icon: BadgeDollarSign },
  ] },
  { label: 'Program', items: [
    { to: '/admin/rules', label: 'Rules', icon: SlidersHorizontal },
    { to: '/admin/catalog', label: 'Catalog', icon: Gift },
    { to: '/admin/engage', label: 'Engage', icon: Megaphone },
    { to: '/admin/coupons', label: 'Coupons', icon: TicketPercent },
  ] },
  { label: 'System', items: [
    { to: '/admin/messages', label: 'Messages', icon: MessageSquare },
    { to: '/admin/program', label: 'Program', icon: Settings2 },
    { to: '/admin/events', label: 'Events', icon: Activity },
    { to: '/admin/fraud', label: 'Fraud', icon: ShieldAlert },
  ] },
];

const ALL = GROUPS.flatMap((g) => g.items);

export default function AdminShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUser() || {};
  const [navOpen, setNavOpen] = useState(false); // phone/tablet drawer
  useEffect(() => { setNavOpen(false); }, [location.pathname]);

  const signOut = async () => {
    try {
      await authAPI.logout();
    } catch {
      /* stateless */
    }
    clearSession();
    navigate('/login', { replace: true });
  };

  const pageTitle = t((ALL.find((n) => location.pathname.startsWith(n.to)) || { label: '' }).label);
  const sectionLbl = { color: '#7889c9', fontSize: 10, fontWeight: 600, letterSpacing: '.13em', padding: '14px 8px 6px' };

  const NavItem = ({ to, icon: Icon, label }) => (
    <NavLink
      to={to}
      style={({ isActive }) => ({
        display: 'flex', alignItems: 'center', gap: 11,
        padding: '8px 11px', borderRadius: 6, fontSize: 13, fontWeight: 500, textDecoration: 'none',
        color: isActive ? '#ffffff' : '#abb9e8',
        background: isActive ? 'rgba(255,255,255,0.10)' : 'transparent',
        borderLeft: `3px solid ${isActive ? 'var(--mms-accent)' : 'transparent'}`,
      })}
    >
      <Icon size={16} />
      {t(label)}
    </NavLink>
  );

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--mms-bg)' }}>
      {/* Sidebar */}
      <aside className={`mms-side${navOpen ? ' is-open' : ''}`} style={{ width: 240, flexShrink: 0, background: 'var(--mms-sidebar)', padding: '18px 14px', display: 'flex', flexDirection: 'column', top: 0, height: '100vh', overflowY: 'auto' }}>
        <div style={{ color: '#fff', padding: '4px 6px 6px' }}>
          <Brand size="sm" linkToHome />
        </div>
        {GROUPS.map((g) => (
          <div key={g.label}>
            <div style={sectionLbl}>{t(g.label)}</div>
            <nav style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {g.items.map((n) => <NavItem key={n.to} {...n} />)}
            </nav>
          </div>
        ))}
        {/* Phone: the topbar has no room for Sign out — it lives in the drawer. */}
        <button type="button" onClick={signOut} className="mms-only-phone" style={{ alignItems: 'center', gap: 11, marginTop: 14, padding: '11px', borderRadius: 6, fontSize: 13, fontWeight: 500, color: '#abb9e8', background: 'transparent', border: 0, cursor: 'pointer', fontFamily: 'inherit' }}>
          <LogOut size={16} /> {t('Sign out')}
        </button>
      </aside>
      {navOpen && <div className="mms-side-scrim" onClick={() => setNavOpen(false)} aria-hidden="true" />}

      {/* Main */}
      <div style={{ display: 'flex', minWidth: 0, flex: 1, flexDirection: 'column' }}>
        <header className="mms-topbar" style={{ height: 60, flexShrink: 0, background: 'var(--mms-topbar)', borderBottom: '1px solid var(--mms-border)', display: 'flex', alignItems: 'center', gap: 16, position: 'sticky', top: 0, zIndex: 30 }}>
          <button type="button" className="mms-burger text-mms-muted hover:text-mms-text" onClick={() => setNavOpen(o => !o)} aria-label={t('Menu')} aria-expanded={navOpen}>
            {navOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--mms-text)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pageTitle}</div>
          <div className="mms-topbar-actions" style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0 }}>
            <ModeToggle />
            <button
              onClick={() => setLang(getLang() === 'es' ? 'en' : 'es')}
              className="mms-hit text-mms-muted hover:text-mms-text"
              style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', border: 0, cursor: 'pointer', fontSize: 12.5, fontWeight: 500, fontFamily: 'inherit' }}
              title={t('Language')}
            >
              <Languages size={15} />
              {getLang().toUpperCase()}
            </button>
            <div className="mms-hide-phone" style={{ width: 1, height: 24, background: 'var(--mms-border)' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(10,179,156,0.12)', border: '1px solid rgba(10,179,156,0.35)', color: '#0a8f7e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: 13 }}>
                {(user.name || '?').trim().slice(0, 1).toUpperCase()}
              </div>
              <div style={{ lineHeight: 1.15 }} className="hidden sm:block">
                <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--mms-text)' }}>{user.name || ''}</div>
                <button onClick={signOut} className="text-mms-muted hover:text-mms-text" style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'transparent', border: 0, padding: 0, cursor: 'pointer', fontSize: 10.5, fontFamily: 'inherit' }}>
                  <LogOut size={11} /> {t('Sign out')}
                </button>
              </div>
            </div>
          </div>
        </header>
        <main className="mms-main" style={{ flex: 1, minWidth: 0 }}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
